import { useState } from "react";
import {
  isSameDay, startOfMonth, endOfMonth, addMonths, subMonths,
  startOfWeek, endOfWeek, eachDayOfInterval, isToday, isSameMonth, format,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Compass } from "lucide-react";

interface TourBookingsCalendarProps {
  rows: any[];
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500/15 text-yellow-700 border-yellow-200",
  confirmed: "bg-blue-500/15 text-blue-700 border-blue-200",
};

const statusLabels: Record<string, string> = {
  pending: "Pending",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const paymentMethodLabels: Record<string, string> = {
  cash: "Cash",
  paypal: "PayPal",
  stripe: "Card",
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getCalendarDays(month: Date) {
  const start = startOfWeek(startOfMonth(month));
  const end = endOfWeek(endOfMonth(month));
  return eachDayOfInterval({ start, end });
}

function getBookingsForDay(day: Date, bookings: any[]) {
  return bookings.filter((b) => b.excursion_date && isSameDay(new Date(b.excursion_date + "T12:00:00"), day));
}

export function TourBookingsCalendar({ rows }: TourBookingsCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const filtered = rows.filter(
    (b) => b.status !== "cancelled"
  );

  const days = getCalendarDays(currentMonth);
  const selectedDayBookings = selectedDay ? getBookingsForDay(selectedDay, filtered) : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-lg font-semibold">{format(currentMonth, "MMMM yyyy")}</h3>
        <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <Card>
        <CardContent className="p-2 sm:p-4">
          <div className="grid grid-cols-7 gap-1">
            {WEEKDAYS.map((d) => (
              <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-2">{d}</div>
            ))}

            {days.map((day) => {
              const inMonth = isSameMonth(day, currentMonth);
              const today = isToday(day);
              const dayBookings = getBookingsForDay(day, filtered);
              const hasEvents = dayBookings.length > 0;

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => hasEvents && setSelectedDay(day)}
                  disabled={!hasEvents}
                  className={`
                    relative min-h-[70px] sm:min-h-[85px] rounded-md border p-1 text-left transition-all
                    ${inMonth ? "" : "opacity-30"}
                    ${today ? "ring-2 ring-primary" : ""}
                    ${hasEvents ? "bg-primary/5 cursor-pointer hover:ring-2 hover:ring-primary/50" : "cursor-default"}
                  `}
                >
                  <span className={`text-xs font-medium ${today ? "text-primary font-bold" : "text-foreground"}`}>
                    {format(day, "d")}
                  </span>
                  {hasEvents && (
                    <div className="flex items-center gap-0.5 mt-0.5">
                      <Compass className="h-3 w-3 text-primary" />
                      <span className="text-[10px] font-semibold text-primary">{dayBookings.length}</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-sm bg-primary/30" />
          <span>Excursions scheduled</span>
        </div>
      </div>

      <Dialog open={!!selectedDay} onOpenChange={(open) => !open && setSelectedDay(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDay && format(selectedDay, "EEEE, MMMM d, yyyy")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            {selectedDayBookings.map((b: any) => (
              <div key={b.id} className="rounded-lg border p-3 space-y-1 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-bold text-primary">{b.booking_code || "—"}</span>
                  <Badge variant="outline" className={statusColors[b.status] || ""}>
                    {statusLabels[b.status] || b.status}
                  </Badge>
                </div>
                <div className="font-medium">{b.customer_name}</div>
                <div className="text-xs text-muted-foreground">{b.customer_phone}</div>
                <div className="text-xs">{(b.tours as any)?.title || "—"}</div>
                {b.schedule && <div className="text-xs text-muted-foreground">Schedule: {b.schedule}</div>}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-muted-foreground">
                    {b.pricing_mode === "fixed"
                      ? `${b.adults}A${b.children > 0 ? ` + ${b.children}C` : ""}`
                      : "Package"} · {paymentMethodLabels[b.payment_method] || b.payment_method}
                  </span>
                  <span className="font-semibold text-green-700">${b.total_price}</span>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
